import { JSX } from 'react';
import classNames from 'classnames';
import { IAtcheckboxProps } from './at-checkbox.types';

/**
 * `AtCheckbox` is a controlled, accessible checkbox component used in UI-driven contexts,
 * such as filters in `OrFilteredGrid`. It is not designed for form libraries like React Hook Form.
 *
 * Visually, the native checkbox is hidden using `sr-only`, and styled via `peer` and Tailwind classes
 * to render a custom circular checkbox with a filled center when selected.
 */

export const AtCheckbox = ({
  checked,
  children,
  name,
  value,
  onChange,
}: IAtcheckboxProps): JSX.Element => {
  return (
    <label className="paragraph-md flex cursor-pointer items-center gap-2 text-skin-secondary">
      <input
        type="checkbox"
        name={name}
        value={value}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
        tabIndex={0}
      />
      <span
        aria-hidden="true"
        className={classNames(
          'relative h-7 w-7 flex-shrink-0 rounded-full border-2 transition-all duration-150',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2',
          {
            'border-skin-stroke-primary peer-focus-visible:ring-checkbox-checked':
              checked,
            'border-skin-stroke-secondary peer-focus-visible:ring-checkbox-unchecked':
              !checked,
          },
        )}
      >
        <span
          className={classNames(
            'absolute left-1/2 top-1/2 h-4.5 w-4.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-skin-ui-fill-primary transition-opacity duration-150',
            {
              'opacity-100': checked,
              'opacity-0': !checked,
            },
          )}
        />
      </span>
      {children}
    </label>
  );
};
